import { createSimpleStore } from 'react-simple-reducer'
import api from '../api'
import { toast } from 'react-toastify'
import { createSelector } from 'reselect'

export interface IRequest {
  errorCode: number
  message: string
  fetching: boolean
}

export interface IAuthState {
  usuario: any
  isAuthenticated: boolean
  sessionChecked: boolean
  shouldChangePassword: boolean
  request: IRequest
}

export const initialState: IAuthState = {
  usuario: null,
  isAuthenticated: false,
  sessionChecked: false,
  shouldChangePassword: false,
  request: {
    fetching: false,
    errorCode: null,
    message: '',
  },
}

const authStore = createSimpleStore(
  initialState,
  {
    loginFetch(state) {
      state.request.fetching = true
    },
    loginSuccess(state, payload: { usuario: any }) {
      state.request = {
        ...initialState.request,
        fetching: false,
      }
      state.usuario = { ...payload.usuario, perms: payload.usuario.perms ?? [] }
      state.isAuthenticated = true
    },
    loginError(state, payload: { errorCode: number; message: string }) {
      state.request = {
        errorCode: payload.errorCode,
        message: payload.message,
        fetching: false,
      }
    },
    setSession(state, payload: { usuario: any; shouldChangePassword?: boolean }) {
      state.usuario = payload.usuario
        ? { ...payload.usuario, perms: payload.usuario.perms ?? [] }
        : null
      state.isAuthenticated = !!payload.usuario
      state.sessionChecked = true
      state.shouldChangePassword = !!payload.shouldChangePassword
    },
    passwordDefined(state) {
      state.shouldChangePassword = false
    },
    logout(state) {
      state.usuario = null
      state.isAuthenticated = false
      state.sessionChecked = true
    },
  },
  {
    thunks: {
      login:
        ({ cpf, pwd }: { cpf: string; pwd: string }) =>
        async (dispatch) => {
          dispatch(authStore.actions.loginFetch())
          try {
            const data = await api.Usuarios.login(cpf, pwd)
            dispatch(authStore.actions.loginSuccess({ usuario: data.usuario }))
          } catch (error) {
            const message = error?.response?.data?.message
            toast.error(message)
            dispatch(authStore.actions.loginError({ errorCode: 400, message }))
          }
        },
      checkSession:
        () =>
        async (dispatch) => {
          try {
            const data = await api.Usuarios.getSession()
            dispatch(authStore.actions.setSession({ usuario: data.usuario, shouldChangePassword: data.shouldChangePassword }))
          } catch {
            dispatch(authStore.actions.logout())
          }
        },
      doLogout:
        () =>
        async (dispatch) => {
          try {
            await api.Usuarios.logout()
          } catch {
            // ignore
          }
          dispatch(authStore.actions.logout())
        },
    },
  }
)

export const getUsuarioLogado = (state: IAuthState) => state.usuario ?? null

export function selectPerms(state: IAuthState): string[] {
  return getUsuarioLogado(state)?.perms ?? []
}

export function hasPerm(state: IAuthState, perm: string): boolean {
  return selectPerms(state).includes(perm)
}

export const selectTokenValid = createSelector(
  (state: IAuthState) => state.isAuthenticated,
  (isAuthenticated: boolean) => isAuthenticated
)

export const selectSessionChecked = (state: IAuthState) => state.sessionChecked
export const selectShouldChangePassword = (state: IAuthState) => state.shouldChangePassword

export { authStore }
export const {
  Provider: AuthProvider,
  useDispatch: useAuthDispatch,
  useState: useAuthState,
  useSelector: useAuthSelector,
  thunks: authThunks,
} = authStore
